<script setup lang="ts">
import { storesWhichSupportRecommendationsCalculation } from '@bluebrick/discounts-service-module'
import { Button, Loader, Tooltip, FormFieldLabel, isFinite, useTranslation, Icon } from '@bluebrick/saas-ui'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed, ref, toRefs, watchEffect } from 'vue'
import Telemetry from '@/common/components/Telemetry.vue'
import WarningIcon from '@/common/components/WarningIcon.vue'
import { now, isSameDate, dateTimeFormattedShortWithTime } from '@/common/dateUtils'
import { getRangeDescriptionWithoutTime } from '@/common/utils'
import DateTimePicker from '@/features/eventDetails/components/DateTimePicker.vue'
import Duration from '@/features/eventDetails/components/Duration.vue'
import EventNotes from '@/features/eventDetails/components/EventNotes.vue'
import { findNextSlot } from '@/features/eventDetails/eventDetailsUtils'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import { useEventsStore } from '@/features/events/useEventsStore'
import { useLoadersStore } from '@/features/loaders/useLoadersStore'
import { useEventNotesStore } from '@/features/notes/useEventNotesStore'
import { useCalendarStore } from '@/features/slicers/useCalendarStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import T from '@/translations/T.vue'
import { type StoreData } from '@/types'

const { updateStartDate, updateEndDate, setOptimalEventDuration, changeToOpportunity } = useEventDetailsStore()
const {
	startDate,
	endDate,
	store,
	isUpdateEvent,
	isRecommendedEventDuration,
	products,
	optimalEventDurationInDays,
	optimalEventDurationFormatted,
	maxEventDurationInDays,
	isOptimalEventDurationValid,
	maxEventDurationFormatted,
	eventDuration,
	isEventTooLong,
	isEventInThePast,
	cooldownDuration,
	cooldownStartRaw,
	cooldownEndRaw,
	isFromOpportunity,
	isFromStoreOpportunity,
	isFromMajorStoreOpportunity,
	recommendedStartDate,
	recommendedEndDate,
	isCooldownLengthValid,
	doesEventTriggerCooldown,
	isEventLongerThanOptimal,
	discountConfig
} = storeToRefs(useEventDetailsStore())
const { isUpserting, noteBeingDeleted } = storeToRefs(useEventNotesStore())
const { onAddNote } = useEventNotesStore()
const loadersStore = useLoadersStore()
const { setSelectedDate } = useCalendarStore()
const { storeEvents } = storeToRefs(useEventsStore())
const { trackEvent } = useTelemetry()
const { t } = useTranslation()

const props = defineProps<{
	readonly?: boolean
}>()

const { readonly } = toRefs(props)

const nextSlot = ref()

const selectNextAvailableSlot = () => {
	trackEvent('user', 'Clicked find first available date.', { store: store.value?.displayName })

	if (nextSlot.value) {
		changeToOpportunity(nextSlot.value)
	}
}

const nextSlotTooltipText = computed(() => {
	if (isUpdateEvent.value) {
		return 'This event is being edited, cannot find a new slot.'
	}

	if (products.value.length === 0) {
		return 'Select a product to find an available slot.'
	}

	if (products.value.length > 1) {
		return 'Cannot find a slot for multiple products.'
	}

	if (isFirstSlotSelected.value) {
		return 'First available slot is already set.'
	}

	if (!nextSlot.value) {
		return 'No opportunities identified for selected product.'
	}

	return undefined
})

watchEffect(() => {
	if (store.value && storeEvents.value.has(store.value.id) && products.value.length === 1) {
		nextSlot.value = findNextSlot(storeEvents.value.get(store.value.id) as StoreData, products.value[0].uniqueSkuId, now())

		return
	}

	nextSlot.value = undefined
})

const findNextSlotDisabled = computed(() =>
	Boolean(isUpdateEvent.value || products.value.length !== 1 || !nextSlot.value || isFirstSlotSelected.value)
)

const isFirstSlotSelected = computed(
	() =>
		!!nextSlot.value &&
		!!startDateLocal.value &&
		!!endDateLocal.value &&
		isSameDate(nextSlot.value.start_date, startDateLocal.value) &&
		isSameDate(nextSlot.value.end_date, endDateLocal.value)
)

const startDateLocal = computed({
	get: () => {
		const date = startDate.value ?? now()

		return date.setZone(discountConfig.value.ianaTimeZoneName) as DateTime<true>
	},
	set: (date) => {
		if (date) {
			trackEvent('app', 'Side panel | Set start date.', { selectedDate: date.toISO() ?? '', store: store.value?.displayName })

			updateStartDate(date.toUTC())
		}
	}
})

const endDateLocal = computed({
	get: () => {
		const date = endDate.value?.isValid
			? endDate.value
			: startDate.value?.isValid && isFinite(optimalEventDurationInDays.value)
				? startDate.value.plus({ days: optimalEventDurationInDays.value })
				: now()

		return date.setZone(discountConfig.value.ianaTimeZoneName)
	},
	set: (date) => {
		if (date) {
			trackEvent('app', 'Side panel | Set end date.', { selectedDate: date.toISO() ?? '', store: store.value?.displayName })

			updateEndDate(date.toUTC())
		}
	}
})

const hasRecommendationsDifferentThanCurrentDates = computed(() => recommendedStartDate.value?.isValid || recommendedEndDate.value?.isValid)
const canStartEventOnThisDate = computed(() => !isOptimalEventDurationValid.value || optimalEventDurationInDays.value !== 0)
const isLoadingDates = computed(
	() =>
		loadersStore.isActive('maxDiscountLength') ||
		loadersStore.isActive('recommendedDates') ||
		loadersStore.isActive('opportunityStartDate') ||
		loadersStore.isActive('discountConfig')
)

const isOptimalDurationDifferentFromMaxDuration = computed(() => optimalEventDurationInDays.value !== maxEventDurationInDays.value)

const isStoreWithRecommendationsSupport = store.value?.id && storesWhichSupportRecommendationsCalculation.includes(store.value?.id)

const startDateStoreSpecificTimeZone = computed<
	| {
			short: string
			long: string
	  }
	| undefined
>(() => {
	if (!store.value || !store.value.timeZone || store.value.timeZone === 'local') {
		return undefined
	}

	const dateTime = startDateLocal.value.setZone(store.value.timeZone)

	return {
		short: dateTime.offsetNameShort ?? '',
		long: dateTime.offsetNameLong ?? ''
	}
})

const endDateStoreSpecificTimeZone = computed<
	| {
			short: string
			long: string
	  }
	| undefined
>(() => {
	if (!store.value || !store.value.timeZone || store.value.timeZone === 'local') {
		return undefined
	}

	const dateTime = endDateLocal.value.setZone(store.value.timeZone)

	return {
		short: dateTime.offsetNameShort ?? '',
		long: dateTime.offsetNameLong ?? ''
	}
})

const isStoreZoneDifferentThanLocal = computed(
	() =>
		!!startDateStoreSpecificTimeZone.value &&
		store.value?.timeZone &&
		store.value.timeZone !== 'local' &&
		DateTime.now().offsetNameShort !== DateTime.now().setZone(store.value.timeZone).offsetNameShort
)

const startTimeInLocalTimeZone = computed(() => {
	if (!isStoreZoneDifferentThanLocal.value) {
		return undefined
	}

	return dateTimeFormattedShortWithTime(startDate.value.setZone('local'), true)
})

const endTimeInLocalTimeZone = computed(() => {
	if (!isStoreZoneDifferentThanLocal.value) {
		return undefined
	}

	return dateTimeFormattedShortWithTime(endDate.value.setZone('local'), true)
})

const storeSpecificTimeRulesDescription = computed(() => {
	const translationKey = `upsertEventForm.timeTooltip.storeRules.${store.value?.id}`

	// A slight hack to check if store rules are defined. If `t(key)` returns the value of `key`
	// then it means that there's no translation for such key.
	// A better approach would be to export `has` function from vue-i18n in `useTranslation` in saas-ui
	if (t(translationKey) === translationKey) {
		return
	}

	// We have a specific time rule described in translations
	return t(translationKey)
})

const recommendedStartDateInStoreTimeZone = computed(() => {
	if (!recommendedStartDate.value || !store.value?.timeZone) {
		return
	}

	return recommendedStartDate.value.setZone(store.value.timeZone)
})

const recommendedEndDateInStoreTimeZone = computed(() => {
	if (!recommendedEndDate.value || !store.value?.timeZone) {
		return
	}

	return recommendedEndDate.value.setZone(store.value.timeZone)
})

const cooldownStartDateInStoreTimeZone = computed(() => {
	if (!cooldownStartRaw.value || !store.value?.timeZone) {
		return
	}

	return cooldownStartRaw.value.setZone(store.value.timeZone)
})

const cooldownEndDateInStoreTimeZone = computed(() => {
	if (!cooldownEndRaw.value || !store.value?.timeZone) {
		return
	}

	return cooldownEndRaw.value.setZone(store.value.timeZone)
})
</script>

<template>
	<div>
		<div class="flex items-center justify-between">
			<div class="font-body-l-bold flex items-center">Event Details</div>

			<Telemetry name="Add event note">
				<Button v-if="!readonly" size="xs" variant="quaternary" :disabled="isUpserting || !!noteBeingDeleted" @click="onAddNote">{{
					t('upsertEventForm.addNote')
				}}</Button>
			</Telemetry>
		</div>

		<EventNotes />

		<div class="mt-03 flex flex-col items-start">
			<div class="datesContainer mt-02 flex w-full items-start text-disabled">
				<div>
					<FormFieldLabel class="flex items-center gap-04">
						<div class="flex items-center justify-between">
							<div class="flex items-center">
								<span
									>{{ t('upsertEventForm.startDate')
									}}<span v-if="startDateStoreSpecificTimeZone">&nbsp;({{ startDateStoreSpecificTimeZone.short }})</span>
								</span>
								<Tooltip v-if="store" class="ml-03 !inline-flex" :content="t('upsertEventForm.scrollToDate')">
									<Icon
										name="eye"
										class="inline-block cursor-pointer text-primary-60 hover:text-primary-100"
										@click="setSelectedDate(store.id, startDateLocal.toUTC(), true)"
									/>
								</Tooltip>
							</div>
						</div>
					</FormFieldLabel>

					<Tooltip :content="t('upsertEventForm.majorStoreEventDatesInfo')" :hide-content="!isFromMajorStoreOpportunity" class="dateSelector">
						<DateTimePicker
							v-model="startDateLocal"
							placeholder="From..."
							:disabled="readonly || isFromMajorStoreOpportunity"
							:disabled-hours="discountConfig.startTimeRules.hour.fixed"
							:disabled-minutes="discountConfig.startTimeRules.minute.fixed"
							:class="['dateTime', { 'dateTime__not-optimal': recommendedStartDate?.isValid }]"
						/>
					</Tooltip>

					<div
						v-if="isStoreZoneDifferentThanLocal && startDateStoreSpecificTimeZone"
						class="font-caption-medium ml-03 mt-03 flex items-center text-disabled"
					>
						<Icon name="account-outline" size="0.75" class="mr-02" />

						{{ startTimeInLocalTimeZone }}
					</div>
				</div>

				<div class="mb-02 mt-07 w-full text-center">-</div>

				<div>
					<FormFieldLabel>
						<div class="flex items-center justify-between">
							<span
								>{{ t('upsertEventForm.endDate')
								}}<span v-if="endDateStoreSpecificTimeZone">&nbsp;({{ endDateStoreSpecificTimeZone.short }})</span></span
							>

							<Tooltip v-if="store">
								<Icon name="information-outline" size="1" class="" />

								<template #content>
									<div class="flex flex-col gap-04">
										<T v-if="endDateStoreSpecificTimeZone" path="storeContainer.storeSpecificTimeZoneTooltip">
											<template #storeName>{{ store.displayName }}</template>
											<template #timeZoneName>{{ endDateStoreSpecificTimeZone.long }} ({{ endDateStoreSpecificTimeZone.short }})</template>
										</T>

										<div v-if="storeSpecificTimeRulesDescription">{{ storeSpecificTimeRulesDescription }}</div>
									</div>
								</template>
							</Tooltip>
						</div>
					</FormFieldLabel>

					<Tooltip :content="t('upsertEventForm.majorStoreEventDatesInfo')" :hide-content="!isFromMajorStoreOpportunity" class="dateSelector">
						<DateTimePicker
							v-model="endDateLocal"
							placeholder="Till..."
							:disabled="readonly || isFromMajorStoreOpportunity"
							:disabled-hours="discountConfig.endTimeRules.hour.fixed"
							:disabled-minutes="discountConfig.endTimeRules.minute.fixed"
							:class="['dateTime', { 'dateTime__not-optimal': recommendedEndDate?.isValid }]"
						>
							<template #calendarFooter>
								<Tooltip
									v-if="!readonly"
									content="An event cannot start on selected date."
									:hide-content="isFinite(optimalEventDurationInDays) && optimalEventDurationInDays > 0"
								>
									<Button
										variant="tertiary"
										class="mt-02"
										:disabled="isRecommendedEventDuration || optimalEventDurationInDays === 0 || !isFinite(optimalEventDurationInDays)"
										@click="setOptimalEventDuration"
										>set max event duration</Button
									>
								</Tooltip>
							</template>
						</DateTimePicker>
					</Tooltip>

					<div
						v-if="isStoreZoneDifferentThanLocal && endDateStoreSpecificTimeZone"
						class="font-caption-medium ml-03 mt-03 flex items-center text-disabled"
					>
						<Icon name="account-outline" size="0.75" class="mr-02" />

						{{ endTimeInLocalTimeZone }}
					</div>
				</div>
			</div>

			<Tooltip
				v-if="!readonly && !isFromOpportunity"
				placement="top"
				:content="nextSlotTooltipText"
				:hide-content="!findNextSlotDisabled"
				class="mt-02"
			>
				<Button size="xs" variant="tertiary" :disabled="findNextSlotDisabled" @click="selectNextAvailableSlot">find first available</Button>
			</Tooltip>

			<div v-if="startDateLocal && endDateLocal" class="mt-05">
				<div class="font-caption-medium mb-02 flex items-center text-ink-60">
					<div>Event duration</div>

					<Loader v-if="isLoadingDates" class="ml-03" />
				</div>

				<template v-if="!isLoadingDates">
					<div class="flex w-full items-baseline">
						<div
							v-if="canStartEventOnThisDate"
							:class="[
								'font-body-s-medium mr-03 whitespace-nowrap',
								{ 'text-ink-87': !isEventTooLong && !hasRecommendationsDifferentThanCurrentDates, 'text-message-error': isEventTooLong },
								{ 'text-message-warning': !isEventTooLong && (hasRecommendationsDifferentThanCurrentDates || isEventLongerThanOptimal) }
							]"
						>
							<Duration :duration="eventDuration" short />
						</div>

						<div
							v-if="
								isOptimalEventDurationValid &&
								optimalEventDurationInDays &&
								optimalEventDurationInDays > 0 &&
								!isFromMajorStoreOpportunity &&
								isStoreWithRecommendationsSupport
							"
							class="font-body-s text-ink-60"
						>
							<div :class="{ 'text-message-warning': !isEventTooLong && isEventLongerThanOptimal }">
								{{ isOptimalDurationDifferentFromMaxDuration ? 'Optimal' : 'Max' }} duration from this date is
								<span class="whitespace-nowrap">{{ optimalEventDurationFormatted }}</span>
							</div>

							<div v-if="isOptimalDurationDifferentFromMaxDuration">
								(max allowed duration is <span class="whitespace-nowrap">{{ maxEventDurationFormatted }}</span
								>)
							</div>
						</div>

						<div v-else-if="!readonly && !canStartEventOnThisDate" class="font-body-s text-message-error">
							Cannot start an event on selected date.
						</div>
					</div>

					<template v-if="!readonly">
						<div v-if="isEventInThePast" class="font-caption mt-03 flex items-center gap-03 text-message-warning">
							<WarningIcon /> You can't set up events in the past.
						</div>

						<div
							v-if="recommendedStartDate && recommendedStartDateInStoreTimeZone?.isValid"
							class="font-caption mt-03 flex w-full items-center gap-03"
						>
							<WarningIcon />

							<span class="text-message-warning">
								We recommend changing the start date to
								<span class="font-caption-medium">{{ dateTimeFormattedShortWithTime(recommendedStartDateInStoreTimeZone) }}</span>
							</span>

							<Button size="xs" variant="tertiary" @click="updateStartDate(recommendedStartDate)">Change</Button>
						</div>

						<div v-if="recommendedEndDate && recommendedEndDateInStoreTimeZone?.isValid" class="font-caption flex w-full items-center gap-03">
							<WarningIcon />

							<span class="text-message-warning">
								We recommend changing the end date to
								<span class="font-caption-medium">{{ dateTimeFormattedShortWithTime(recommendedEndDateInStoreTimeZone) }}</span>
							</span>

							<Button size="xs" variant="tertiary" @click="updateEndDate(recommendedEndDate)">Change</Button>
						</div>
					</template>
				</template>

				<template v-if="isCooldownLengthValid && isStoreWithRecommendationsSupport">
					<div class="font-caption-medium mt-04 flex items-center text-ink-60">Event cooldown</div>

					<div v-if="doesEventTriggerCooldown" class="mt-02 flex items-center">
						<div class="font-body-s-medium mr-03 flex items-center">
							<Duration :duration="cooldownDuration" short />
						</div>

						<div
							v-if="!isFromStoreOpportunity && cooldownStartDateInStoreTimeZone?.isValid && cooldownEndDateInStoreTimeZone?.isValid"
							class="font-body-s text-ink-60"
						>
							{{ getRangeDescriptionWithoutTime(cooldownStartDateInStoreTimeZone, cooldownEndDateInStoreTimeZone) }}
						</div>
					</div>

					<div v-else class="font-body-s-medium">This event does not generate any cooldown.</div>
				</template>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.datesContainer {
	:deep(.dateSelector.popover-toggler) {
		justify-content: start;
	}
}

.dateTime {
	&__not-optimal {
		:deep(.calendarInputDateLabel) {
			color: theme('colors.message.warning');
		}
	}
}
</style>
